import { animated, useSpring } from '@react-spring/web';
import { useMedia } from 'react-use';
import { Paragraph } from '@hungryroot/ramen';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  ROUTE_BREAKFAST,
  ROUTE_BREAKFAST_FREQUENCY,
  ROUTE_MACRO_NUTRIENT,
  ROUTE_MACRO_NUTRIENT_LOW_SODIUM,
  ROUTE_QUIZ_DINNER,
  ROUTE_QUIZ_DINNER_FREQUENCY,
  ROUTE_QUIZ_LUNCH,
  ROUTE_QUIZ_LUNCH_FREQUENCY,
  ROUTE_SNACKS,
  ROUTE_SNACKS_FREQUENCY,
  ROUTE_SWEETS,
  ROUTE_SWEETS_FREQUENCY,
} from '../../defs/routes';
import { type RouteGroup } from '../../main';

import styles from './ProgressBar.module.css';
interface Props<T extends string> {
  testid?: string;
  className?: string;
  sections: RouteGroup<T>[];
  currentPathName: T;
}

interface BarProps<T extends string> {
  page: T;
  section: RouteGroup<T>;
  sections: RouteGroup<T>[];
}

const getDesktopProgress = <T extends string>(
  page: T,
  sections: RouteGroup<T>[],
  section: RouteGroup<T>,
  index: number
) => {
  const totalPages = section.pages.length;
  if (section.type === 'about') {
    return section.pages.includes(page)
      ? section.pages.indexOf(page) + 1
      : totalPages;
  } else if (section.type === 'tastes') {
    return index >= totalPages + sections[0].pages.length
      ? totalPages
      : section.pages.indexOf(page) + 1;
  } else {
    return section.pages.indexOf(page) + 1;
  }
};

function BarAnimation<T extends string>(props: BarProps<T>) {
  const { section, sections, page } = props;
  const navigationProgressBar = sections.reduce(
    (acc, section) => {
      return [...acc, ...section.pages];
    },
    [] as RouteGroup<T>['pages']
  );

  // Default of `true` because if we are on Desktop and have a default of `false` it triggers a re-render where
  // React-Spring animations change. With a default of `true`, even on mobile, the reverse isn't a problem.
  const isDesktop = useMedia(BreakpointQueryLarge, true);
  const index = navigationProgressBar.indexOf(page);
  const value = getDesktopProgress(page, sections, section, index);

  const spring = useSpring({
    value: isDesktop ? value : index + 1,
  });

  return (
    <div className={styles.container}>
      <Paragraph
        variant="caption"
        className={styles.header}
        testid="progress-bar-text"
      >
        {section.header}
      </Paragraph>
      <animated.progress
        className={styles.progress}
        data-testid="animated-progress-bar"
        value={spring.value}
        max={isDesktop ? section.pages.length : navigationProgressBar.length}
      />
    </div>
  );
}

const mobileSection = <T extends string>(
  sections: RouteGroup<T>[],
  page: T
) => {
  if (sections[0].pages.includes(page)) return sections[0];
  else if (sections[1].pages.includes(page)) return sections[1];
  else return sections[2];
};

/** If a route should be considered part of another page, map the route to the parent page here */
const pageReMap = {
  [ROUTE_QUIZ_LUNCH_FREQUENCY]: ROUTE_QUIZ_LUNCH,
  [ROUTE_QUIZ_DINNER_FREQUENCY]: ROUTE_QUIZ_DINNER,
  [ROUTE_BREAKFAST_FREQUENCY]: ROUTE_BREAKFAST,
  [ROUTE_SNACKS_FREQUENCY]: ROUTE_SNACKS,
  [ROUTE_SWEETS_FREQUENCY]: ROUTE_SWEETS,
  [ROUTE_MACRO_NUTRIENT_LOW_SODIUM]: ROUTE_MACRO_NUTRIENT,
};

type PageReMap = typeof pageReMap;

export function ProgressBar<T extends string>(props: Props<T>) {
  const { testid, className, sections, currentPathName: pathName } = props;
  const isDesktop = useMedia(BreakpointQueryLarge, false);

  const page: T = (pageReMap[pathName as keyof PageReMap] as T) ?? pathName;

  return (
    <div
      className={`${styles['outer-container']} ${className}`}
      data-testid={testid}
    >
      {isDesktop ? (
        <div className={styles['desktop-container']}>
          <BarAnimation sections={sections} page={page} section={sections[0]} />
          <BarAnimation sections={sections} page={page} section={sections[1]} />
          <BarAnimation sections={sections} page={page} section={sections[2]} />
        </div>
      ) : (
        <BarAnimation
          sections={sections}
          page={page}
          section={mobileSection(sections, page)}
        />
      )}
    </div>
  );
}
